import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, ShieldCheck, Wrench, Bell } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";

/**
 * Mechanic onboarding intro — shown once before the registration form when a
 * user picks the "mechanic" role. Sets a localStorage flag so it doesn't
 * re-appear on subsequent visits.
 */
export default function MechanicIntro() {
  const navigate = useNavigate();

  const continueToRegister = () => {
    try { localStorage.setItem("afr_mechanic_intro_seen", "1"); } catch { /* noop */ }
    navigate("/register?role=mechanic", { replace: true });
  };

  return (
    <div className="mobile-shell">
      <div className="px-6 pt-6 pb-2 flex items-center gap-3">
        <Link to="/" data-testid="mech-intro-back"><ArrowLeft /></Link>
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
          Become a Mechanic
        </span>
      </div>

      <div className="px-6 pt-4 pb-6 text-center">
        <div className="mx-auto w-20 h-20 rounded-full overflow-hidden bg-white/5 ring-2 ring-[#3CFF7E]/40 mb-5">
          <img src={LOGO_URL} alt="AutoFix Rescue" className="w-full h-full object-cover scale-110" />
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight leading-tight">
          Help drivers nearby.<br />Earn on your terms.
        </h1>
        <p className="text-sm text-[#9CA3AF] mt-3 max-w-sm mx-auto leading-relaxed">
          AutoFix matches you with stranded drivers in your area using your live location.
          Set your hours, accept jobs you want, get paid after each one.
        </p>
      </div>

      <div className="px-6 space-y-3 pb-6">
        <Step
          icon={<MapPin size={20} />}
          title="Why GPS is required"
          body="AutoFix uses your location only to connect nearby clients. We never share your exact address — only your distance to the job."
        />
        <Step
          icon={<Wrench size={20} />}
          title="Choose your specialities"
          body="Towing, jumpstarts, diagnostics — list what you do, and you'll only get matched to jobs you can handle."
        />
        <Step
          icon={<Bell size={20} />}
          title="Get notified instantly"
          body="When a driver near you needs help, your phone buzzes. Tap to accept — no scrolling through job boards."
        />
        <Step
          icon={<ShieldCheck size={20} />}
          title="Verified & protected"
          body="All mechanics are reviewed by AutoFix admin before going live. We've got your back on payments and disputes."
        />
      </div>

      <div className="px-6 pb-10 space-y-3">
        <button
          onClick={continueToRegister}
          data-testid="mech-intro-continue"
          className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 active:scale-[0.98] shadow-[0_0_40px_rgba(60,255,126,0.35)]"
        >
          Continue <ArrowRight size={18} />
        </button>
        <Link
          to="/register"
          data-testid="mech-intro-skip"
          className="block w-full h-12 text-center leading-[3rem] text-[#9CA3AF] text-sm font-display font-bold uppercase tracking-wider"
        >
          I'm a Driver Instead
        </Link>
      </div>
    </div>
  );
}

function Step({ icon, title, body }) {
  return (
    <div className="border border-white/10 bg-[#111827] rounded-md p-4 flex gap-3">
      <div className="shrink-0 w-10 h-10 rounded-md bg-[#3CFF7E]/10 border border-[#3CFF7E]/30 flex items-center justify-center text-[#3CFF7E]">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-display font-bold text-sm text-white">{title}</div>
        <div className="text-xs text-[#9CA3AF] mt-0.5 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
